/**
 * Shared formatting helpers for the YaaS Client Console pages.
 *
 * Consolidates the formatters that used to live in `AddPosition/format.ts`
 * and `Dashboard/format.ts` (and a few inline copies). Currency / percent
 * conventions match the dashboard's copy rules — e.g. `+$47` /
 * `+0.3% APY`.
 */

import { Base58Util } from "@solomon-labs/base58";

/* ── Currency ───────────────────────────────────────────── */

export function formatUsd(value: number, options?: { fractionDigits?: 0 | 2 }): string {
    const fractionDigits = options?.fractionDigits ?? 2;
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
    }).format(value);
}

export function formatUsdCompact(value: number): string {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        notation: "compact",
        maximumFractionDigits: 1
    }).format(value);
}

export function formatDeltaUsd(value: number): string {
    const sign = value > 0 ? "+" : value < 0 ? "−" : "";
    return `${sign}${formatUsd(Math.abs(value), { fractionDigits: 0 })}`;
}

/* ── Percent ────────────────────────────────────────────── */

export function formatPct(value: number, fractionDigits = 2): string {
    return `${value.toFixed(fractionDigits)}%`;
}

export function formatDeltaPct(value: number, fractionDigits = 2): string {
    const sign = value > 0 ? "+" : value < 0 ? "−" : "";
    return `${sign}${Math.abs(value).toFixed(fractionDigits)}%`;
}

/**
 * APY string to show for a Program Surface: its own backend-computed trailing 30-day
 * APY when present, otherwise the protocol's advertised staking APY (USDv
 * account / wallet Program Surfaces don't carry a surface-level trailing APY — their
 * yield is the staking APY resolved via the shared `useAdvertisedApy`
 * waterfall and passed in as `protocolApy`).
 */
export function formatProgramSurfaceApy(trailingApyPct: string | null, protocolApy: string): string {
    return trailingApyPct ? formatPct(parseFloat(trailingApyPct), 2) : protocolApy;
}

/* ── Addresses + hashes ──────────────────────────────────── */

export function truncateAddress(addr: string, leading = 4, trailing = 4): string {
    if (!addr) return "";
    if (addr.length <= leading + trailing + 1) return addr;
    return `${addr.slice(0, leading)}…${addr.slice(-trailing)}`;
}

export function truncateHash(sig: string, leading = 6, trailing = 6): string {
    if (sig.length <= leading + trailing + 2) return sig;
    return `${sig.slice(0, leading)}…${sig.slice(-trailing)}`;
}

export function solscanTxUrl(sig: string): string {
    return `https://solscan.io/tx/${sig}`;
}

/* ── Dates ──────────────────────────────────────────────── */

type DateInput = number | string | Date;

/** Month + day. e.g. `Jan 15`. */
export function formatShortDate(value: DateInput): string {
    return new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric"
    });
}

/** Month + day + year. e.g. `Jan 15, 2026`. */
export function formatLongDate(value: DateInput): string {
    return new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
    });
}

/**
 * Human-readable relative-day phrasing for ISO deadlines:
 *   `today` / `in 1 day` / `in 5 days` / `1 day overdue` / `5 days overdue`.
 */
export function formatRelativeDays(iso: string): string {
    const target = new Date(iso).getTime();
    const now = Date.now();
    const days = Math.ceil((target - now) / (1000 * 60 * 60 * 24));
    if (days === 0) return "today";
    if (days === 1) return "in 1 day";
    if (days > 1) return `in ${days} days`;
    if (days === -1) return "1 day overdue";
    return `${Math.abs(days)} days overdue`;
}

/* ── Program Surface labels ─────────────────────────────── */

export function programSurfaceTypeLabel(type: string): string {
    switch (type) {
        case "DEX_LP":
            return "DEX LP";
        case "TREASURY":
            return "Address";
        case "NEOBANK":
            return "Neobank";
        case "LENDING":
            return "Lending";
        case "WALLET":
            return "Wallet";
        default:
            return type;
    }
}

/* ── Address validation ─────────────────────────────────── */

const SOLANA_ADDRESS_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

/**
 * A Solana address: base58 that decodes to exactly 32 bytes. The pattern
 * alone (32 to 44 base58 characters) passes strings that are no key at all,
 * e.g. a 33-character one that decodes to 24 bytes.
 */
export function isValidSolanaAddress(addr: string): boolean {
    return SOLANA_ADDRESS_RE.test(addr) && Base58Util.isValidPublicKey(addr);
}

/**
 * Whether an address being typed is already known to be wrong: a character
 * base58 has no use for (0, O, I, l, or anything else), more than 44
 * characters, or the shortest address length reached without decoding to a
 * key. Shorter, it may just be unfinished.
 */
export function isSettledInvalidAddress(addr: string): boolean {
    if (!addr || isValidSolanaAddress(addr)) return false;
    return !/^[1-9A-HJ-NP-Za-km-z]*$/.test(addr) || addr.length >= 32;
}

/**
 * Heuristic check for known exchange deposit address prefixes.
 * Not authoritative — just a heads-up for the user.
 */
const EXCHANGE_PREFIXES = ["Bnb", "Cb1", "Kkn", "Bif"];

export function looksLikeExchangeAddress(addr: string): boolean {
    return EXCHANGE_PREFIXES.some((prefix) => addr.startsWith(prefix));
}
